import {
  bump,
  crossings,
  firstCrossing,
  ramp,
  waves,
} from "features/onboarding/visuals/live/path";
import {
  clamp01,
  easeInOut,
  easeOut,
  FillEvent,
  LEAD,
  LevelView,
  Story,
} from "features/onboarding/visuals/live/story";
import type { VisualMethod } from "features/onboarding/visuals/types";

/** The scenes' words come from the onboarding locale (`visual.*`). */
export type Tr = (key: string, options?: Record<string, unknown>) => string;

const usd = (value: number): string =>
  (value > 0.004 ? "+$" : "$") + value.toFixed(2);
const whole = (value: number): string =>
  Math.round(value).toLocaleString("en-US");
const tenth = (value: number): string => value.toFixed(1);

/** The draw-in of order lines placed at `at`, one after another. */
const drawIn = (t: number, at: number, index = 0): number =>
  easeOut((t - at - index * 0.08) / 0.6);

/** A value that moves to each next value shortly after its time. */
const stepped = (t: number, steps: [time: number, value: number][]): number => {
  let value = steps[0][1];
  for (let i = 1; i < steps.length; i++) {
    const [at, next] = steps[i];
    value += (next - steps[i - 1][1]) * easeInOut((t - at) / 0.6);
  }
  return value;
};

/** The price range a story shows: its price and levels, with some air. */
const rangeOf = (
  price: (t: number) => number,
  loop: number,
  values: number[],
  pad = 0.1,
  from = -3
): [number, number] => {
  let lo = Math.min(...values);
  let hi = Math.max(...values);
  // The drawn part: the fixed window's past (or from `from`), up to the
  // line's rest.
  for (let t = Math.max(-3, from); t <= loop - 1.5; t += 0.05) {
    lo = Math.min(lo, price(t));
    hi = Math.max(hi, price(t));
  }
  const air = (hi - lo) * pad;
  return [lo - air, hi + air];
};

/* ------------------------------------------------------------------------ */
/* Grid                                                                      */
/* ------------------------------------------------------------------------ */

const GRID_LEVELS = 7;
const GRID_PRICE0 = 67250;
const GRID_STEP = 250;
const GRID_PROFIT = 6.42;
/** When the grid's orders are in place (the lines have drawn in). */
const GRID_READY = 0.6;

/**
 * Grid rule (as real grid bots run): every line holds one order except the
 * line nearest the price. Lines below hold buys, lines above hold sells. When
 * a buy fills, its sell goes one line up; when a sell fills, a buy goes one
 * line down.
 */
const gridFills = (
  price: (t: number) => number,
  until: number
): { t: number; level: number; side: "buy" | "sell" }[] => {
  const start = price(GRID_READY);
  const empty = Math.round(Math.min(GRID_LEVELS - 1, Math.max(0, start)));
  const orders = Array.from({ length: GRID_LEVELS }, (_, i) =>
    i === empty ? null : i < empty ? "buy" : "sell"
  );
  const all = Array.from({ length: GRID_LEVELS }, (_, level) =>
    crossings(price, level, GRID_READY, until).map((c) => ({ ...c, level }))
  )
    .flat()
    .sort((a, b) => a.t - b.t);
  const fills: { t: number; level: number; side: "buy" | "sell" }[] = [];
  for (const c of all) {
    if (c.dir === "down" && orders[c.level] === "buy") {
      fills.push({ t: c.t, level: c.level, side: "buy" });
      orders[c.level] = null;
      orders[c.level + 1] = "sell";
    } else if (c.dir === "up" && orders[c.level] === "sell") {
      fills.push({ t: c.t, level: c.level, side: "sell" });
      orders[c.level] = null;
      orders[c.level - 1] = "buy";
    }
  }
  return fills;
};

export const gridStory = (tr: Tr): Story => {
  // Level units: 0 is the lowest line, 6 the highest.
  const price = (t: number) =>
    2.48 +
    waves([
      [0.92, 6.6, 0.6],
      [0.16, 13.5, 2.2],
    ])(t);
  const raw = gridFills(price, 30);
  const [buy1, sell1, buy2, sell2] = raw;
  const H = [0, buy1.t - LEAD, sell1.t - LEAD, buy2.t - LEAD];
  const loop = sell2.t + 2.8;
  const used = raw.filter((f) => f.t < loop);
  const fills: FillEvent[] = used.map((f) => ({
    t: f.t,
    value: f.level,
    tone: f.side === "buy" ? "up" : "accent",
    level: `l${f.level}`,
    letter: f.side === "buy" ? "B" : "S",
    profit: f.side === "sell" ? usd(GRID_PROFIT) : undefined,
  }));
  const ordersAt = (t: number) => {
    const empty = Math.round(price(GRID_READY));
    const o = Array.from({ length: GRID_LEVELS }, (_, i) =>
      i === empty ? null : i < empty ? "buy" : "sell"
    );
    for (const f of used) {
      if (t < f.t) break;
      o[f.level] = null;
      if (f.side === "buy") o[f.level + 1] = "sell";
      else o[f.level - 1] = "buy";
    }
    return o;
  };
  const top = GRID_PRICE0 + (GRID_LEVELS - 1) * GRID_STEP;
  return {
    H,
    loop,
    price,
    range: [-0.45, GRID_LEVELS - 1 + 0.45],
    format: (v) => whole(GRID_PRICE0 + v * GRID_STEP),
    levels: (t) => {
      const orders = ordersAt(t);
      return orders.map(
        (side, i): LevelView => ({
          key: `l${i}`,
          value: i,
          tone: side === "buy" ? "up" : side === "sell" ? "accent" : "muted",
          state: side ? "pending" : "empty",
          shown: easeOut((t - 0.15 - Math.abs(i - 3) * 0.08) / 0.7),
        })
      );
    },
    fills,
    header: {
      pair: "BTC/USDT",
      coin: { glyph: "₿", color: "#F7931A" },
      meta: tr("visual.grid.meta", {
        n: GRID_LEVELS,
        low: whole(GRID_PRICE0),
        high: whole(top),
      }),
    },
    status: () => ({ text: tr("visual.grid.status") }),
    stats: (t) => {
      const done = fills.filter((f) => f.t <= t);
      const profit = done
        .filter((f) => f.letter === "S")
        .reduce((sum, f) => sum + GRID_PROFIT * easeOut((t - f.t) / 0.6), 0);
      return [
        { label: tr("visual.grid.trades_label"), value: String(done.length) },
        {
          label: tr("visual.grid.profit_label"),
          value: usd(profit),
          positive: profit > 0.004,
        },
      ];
    },
  };
};

/* ------------------------------------------------------------------------ */
/* Signals                                                                   */
/* ------------------------------------------------------------------------ */

const SIG = { t2: 149, t1: 144.5, e1: 142, e2: 138, sl: 133, avg: 140 };
/** When the signal's orders are placed (after the message has been read). */
const SIG_READY = 1.2;

export const signalsStory = (tr: Tr): Story => {
  // A dip through both entries, then a rally through both targets.
  const price = (t: number) =>
    144.06 -
    6.6 * bump(t, 5.08, 3.54) +
    4.24 * ramp(t, 6.74, 1.5) +
    2.64 * ramp(t, 11.2, 5.94) +
    waves([[0.11, 6.56, 3]])(t);
  const e1 = firstCrossing(price, SIG.e1, "down", SIG_READY, 20);
  const e2 = firstCrossing(price, SIG.e2, "down", e1, 20);
  const t1 = firstCrossing(price, SIG.t1, "up", e2, 25);
  const t2 = firstCrossing(price, SIG.t2, "up", t1, 30);
  const H = [0, e1 - LEAD, t1 - LEAD, t2 - LEAD];
  const loop = t2 + 3.5;
  const slide = t1 + 0.35;
  const profits = [12.86, 25.71];
  const fills: FillEvent[] = [
    { t: e1, value: SIG.e1, tone: "accent", level: "e1" },
    { t: e2, value: SIG.e2, tone: "accent", level: "e2" },
    { t: t1, value: SIG.t1, tone: "up", level: "t1", profit: usd(profits[0]) },
    { t: t2, value: SIG.t2, tone: "up", level: "t2", profit: usd(profits[1]) },
  ];
  const filled = (key: string, t: number) =>
    fills.some((f) => f.level === key && f.t <= t);
  const order = (
    key: keyof typeof SIG,
    tone: LevelView["tone"],
    name: string,
    index: number,
    t: number,
    short?: string
  ): LevelView => ({
    key,
    value: SIG[key],
    tone,
    name,
    short,
    state: filled(key, t) ? "filled" : t >= t2 ? "empty" : "pending",
    shown: drawIn(t, SIG_READY, index),
  });
  return {
    H,
    loop,
    price,
    range: rangeOf(price, loop, Object.values(SIG), 0.06),
    format: tenth,
    levels: (t) => {
      const moved = easeInOut((t - slide) / 0.7);
      const views: LevelView[] = [
        order(
          "t2",
          "up",
          tr("visual.signals.target_n", { n: 2 }),
          0,
          t,
          tr("visual.live.short.tp_n", { n: 2 })
        ),
        order(
          "t1",
          "up",
          tr("visual.signals.target_n", { n: 1 }),
          1,
          t,
          tr("visual.live.short.tp_n", { n: 1 })
        ),
        order("e1", "accent", tr("visual.signals.entry_n", { n: 1 }), 2, t),
        order("e2", "accent", tr("visual.signals.entry_n", { n: 2 }), 3, t),
        {
          key: "sl",
          value: SIG.sl + (SIG.avg - SIG.sl) * moved,
          tone: "stop",
          name: tr(
            moved > 0.5
              ? "visual.signals.breakeven"
              : "visual.signals.stop_loss"
          ),
          short: tr(
            moved > 0.5
              ? "visual.live.short.breakeven"
              : "visual.live.short.stop_loss"
          ),
          state: t >= t2 ? "empty" : "pending",
          shown: drawIn(t, SIG_READY, 4),
        },
      ];
      const avg = clamp01((t - e2 - 0.2) / 0.4);
      const fade = 1 - clamp01((t - slide) / 0.4);
      if (avg > 0 && fade > 0)
        views.push({
          key: "avg",
          value: SIG.avg,
          tone: "ink",
          name: tr("visual.signals.average"),
          state: "line",
          shown: avg,
          fade,
        });
      return views;
    },
    fills,
    result: { fill: 3, text: tr("visual.signals.result") },
    note: {
      level: "sl",
      from: slide + 0.2,
      to: slide + 2.4,
      text: tr("visual.signals.sl_breakeven"),
    },
    header: {
      pair: "SOL/USDT",
      coin: { glyph: "S", color: "#8A5CF6" },
      meta: tr("visual.signals.label"),
    },
    status: (t) =>
      t >= t2
        ? { text: tr("visual.signals.status_closed"), closed: true }
        : t >= e1
        ? { text: tr("visual.signals.status_trade") }
        : { text: tr("visual.signals.status") },
    stats: (t) => {
      const profit = fills
        .filter((f) => f.profit && f.t <= t)
        .reduce((sum, f, i) => sum + profits[i] * easeOut((t - f.t) / 0.6), 0);
      return [
        {
          label: tr("visual.live.filled"),
          value: `${fills.filter((f) => f.t <= t).length}/4`,
        },
        {
          label: tr("visual.live.profit"),
          value: usd(profit),
          positive: profit > 0.004,
        },
      ];
    },
    strip: {
      icon: "radio",
      title: tr("visual.signals.channel"),
      items: [
        { value: "#SOL/USDT", tag: tr("visual.signals.long") },
        {
          label: tr("visual.signals.entry"),
          value: `${tenth(SIG.e1)} · ${tenth(SIG.e2)}`,
        },
        {
          label: tr("visual.signals.targets"),
          value: `${tenth(SIG.t1)} · ${tenth(SIG.t2)}`,
        },
        { label: tr("visual.signals.stop_loss"), value: tenth(SIG.sl) },
      ],
      at: [0.15, 0.4, 0.65, 0.9],
    },
  };
};

/* ------------------------------------------------------------------------ */
/* DCA                                                                       */
/* ------------------------------------------------------------------------ */

const DCA_OPEN = 0.9;
/** How long the plot takes to rescale (s). */
const RESCALE = 0.6;
const DCA_TP = 0.015;
const DCA_BUYS = [62000, 60400];
/** Order sizes (the first buy is 1). */
const DCA_SIZES = [1, 2, 3];

export const dcaStory = (tr: Tr): Story => {
  // A slow slide through both extra buys, then a rebound to the take-profit.
  const price = (t: number) =>
    64080 -
    4015 * ramp(t, 3.65, 6.17) +
    3104 * ramp(t, 9.62, 2.62) +
    waves([[260, 4.28, 0.27]])(t);
  const b1 = price(DCA_OPEN);
  const levels = [b1, ...DCA_BUYS];
  const avgAfter = (n: number) => {
    let cost = 0;
    let size = 0;
    for (let i = 0; i < n; i++) {
      cost += levels[i] * DCA_SIZES[i];
      size += DCA_SIZES[i];
    }
    return cost / size;
  };
  const avgs = [1, 2, 3].map(avgAfter);
  const tps = avgs.map((a) => a * (1 + DCA_TP));
  const b2 = firstCrossing(price, DCA_BUYS[0], "down", DCA_OPEN, 20);
  const b3 = firstCrossing(price, DCA_BUYS[1], "down", b2, 25);
  const close = firstCrossing(price, tps[2], "up", b3, 30);
  const H = [0, b2 - LEAD, b3 - LEAD, close - LEAD];
  const loop = close + 3.4;
  const profit = 90;
  const fills: FillEvent[] = [
    { t: DCA_OPEN, value: b1, tone: "accent", level: "b1" },
    { t: b2, value: DCA_BUYS[0], tone: "accent", level: "b2" },
    { t: b3, value: DCA_BUYS[1], tone: "accent", level: "b3" },
    { t: close, value: tps[2], tone: "up", level: "tp", profit: usd(profit) },
  ];
  const lag = 0.15;
  const avgAt = (t: number) =>
    stepped(t, [
      [0, avgs[0]],
      [b2 + lag, avgs[1]],
      [b3 + lag, avgs[2]],
    ]);
  const tpAt = (t: number) =>
    stepped(t, [
      [0, tps[0]],
      [b2 + lag, tps[1]],
      [b3 + lag, tps[2]],
    ]);
  const wide = rangeOf(price, loop, [...levels, tps[0]], 0.06);
  const rescaleAt = b2 + lag;
  // Once rescaled, only the price still on screen counts: all of the past in
  // the fixed window, the last few seconds in the scrolling one.
  const tightRanges = new Map<number, [number, number]>();
  const tight = (past: number) => {
    const key = Number.isFinite(past) ? Math.round(past * 10) : -1;
    let range = tightRanges.get(key);
    if (!range) {
      range = rangeOf(
        price,
        loop,
        [...levels, tps[1], tps[2]],
        0.06,
        rescaleAt - past
      );
      tightRanges.set(key, range);
    }
    return range;
  };
  return {
    H,
    loop,
    price,
    range: rangeOf(price, loop, [...levels, ...tps], 0.06),
    // The first take-profit sits above everything else; once the second buy
    // brings it down, the plot rescales instead of keeping an empty band.
    rangeAt: (t, past) => {
      const k = Math.min(1, Math.max(0, (t - rescaleAt) / RESCALE));
      const e = k * k * (3 - 2 * k);
      const [lo, hi] = tight(past);
      return [wide[0] + (lo - wide[0]) * e, wide[1] + (hi - wide[1]) * e];
    },
    format: (v) => whole(Math.round(v / 10) * 10),
    levels: (t) => {
      const views: LevelView[] = levels.map((value, i) => ({
        key: `b${i + 1}`,
        value,
        tone: "accent",
        name: tr("visual.dca.buy_n", { n: i + 1 }),
        state: fills[i].t <= t ? "filled" : t >= close ? "empty" : "pending",
        shown: drawIn(t, 0.2, i),
      }));
      const after = easeOut((t - DCA_OPEN - 0.15) / 0.5);
      views.push(
        {
          key: "avg",
          value: avgAt(t),
          tone: "ink",
          name: tr("visual.signals.average"),
          state: "line",
          shown: after,
          fade: 1 - clamp01((t - close - 0.4) / 0.4),
        },
        {
          key: "tp",
          value: tpAt(t),
          tone: "up",
          name: tr("visual.tradingview.take_profit"),
          short: tr("visual.live.short.take_profit"),
          state: t >= close ? "filled" : "pending",
          shown: after,
        }
      );
      return views;
    },
    fills,
    result: { fill: 3, text: tr("visual.dca.win") },
    header: {
      pair: "BTC/USDT",
      coin: { glyph: "₿", color: "#F7931A" },
      meta: tr("visual.dca.meta"),
    },
    status: (t) =>
      t >= close
        ? { text: tr("visual.dca.status_closed"), closed: true }
        : { text: tr("visual.dca.status") },
    stats: (t) => {
      const filledBuys = fills.slice(0, 3).filter((f) => f.t <= t).length;
      const earned = t >= close ? profit * easeOut((t - close) / 0.6) : 0;
      return [
        {
          label: tr("visual.live.avg_price"),
          value: filledBuys === 0 ? "—" : whole(Math.round(avgAt(t) / 10) * 10),
        },
        {
          label: tr("visual.live.profit"),
          value: usd(earned),
          positive: earned > 0.004,
        },
      ];
    },
  };
};

/* ------------------------------------------------------------------------ */
/* TradingView                                                               */
/* ------------------------------------------------------------------------ */

const TV_TP = 0.035;
const TV_SL = 0.02;
/** From the alert to the bot's order (the webhook, then the exchange). */
const TV_DELAY = 2.2;

export const tradingViewStory = (tr: Tr): Story => {
  // A fall into a low (where RSI turns back up and the alert fires), then a climb.
  const price = (t: number) =>
    3236 +
    200 * (1 - ramp(t, 0, 4)) +
    175 * ramp(t, 6.2, 4) +
    waves([
      [24, 4.4, 2.04],
      [8, 9, 0.7],
    ])(t);
  // The alert fires at the low: where the price turns back up.
  const dp = (t: number) => price(t + 0.001) - price(t - 0.001);
  const alert = crossings(dp, 0, 0.6, 6).find((c) => c.dir === "up")!.t;
  const entryAt = alert + TV_DELAY;
  const entry = price(entryAt);
  const tp = entry * (1 + TV_TP);
  const sl = entry * (1 - TV_SL);
  const close = firstCrossing(price, tp, "up", entryAt, 30);
  const H = [0, entryAt - LEAD, close - LEAD];
  const loop = close + 4.5;
  const profit = 35;
  const placed = entryAt - 0.15;
  const fills: FillEvent[] = [
    { t: entryAt, value: entry, tone: "accent", level: "entry" },
    { t: close, value: tp, tone: "up", level: "tp", profit: usd(profit) },
  ];
  return {
    H,
    loop,
    price,
    range: rangeOf(price, loop, [entry, tp, sl], 0.06),
    format: whole,
    levels: (t) => [
      {
        key: "tp",
        value: tp,
        tone: "up",
        name: tr("visual.tradingview.take_profit"),
        short: tr("visual.live.short.take_profit"),
        state: t >= close ? "filled" : "pending",
        shown: drawIn(t, placed, 0),
      },
      {
        key: "entry",
        value: entry,
        tone: "accent",
        name: tr("visual.tradingview.entry"),
        state: t >= entryAt ? "filled" : "pending",
        shown: drawIn(t, placed, 1),
      },
      {
        key: "sl",
        value: sl,
        tone: "stop",
        name: tr("visual.tradingview.stop_loss"),
        short: tr("visual.live.short.stop_loss"),
        state: t >= close ? "empty" : "pending",
        shown: drawIn(t, placed, 2),
      },
    ],
    fills,
    markers: [{ t: alert, value: price(alert) }],
    result: { fill: 1, text: tr("visual.tradingview.result") },
    header: {
      pair: "ETH/USDT",
      coin: { glyph: "Ξ", color: "#627EEA" },
      meta: tr("visual.tradingview.label"),
    },
    status: (t) =>
      t >= close
        ? { text: tr("visual.tradingview.status_closed"), closed: true }
        : t >= entryAt
        ? { text: tr("visual.tradingview.status_trade") }
        : { text: tr("visual.tradingview.status") },
    stats: (t) => {
      const earned = t >= close ? profit * easeOut((t - close) / 0.6) : 0;
      return [
        {
          label: tr("visual.live.alerts"),
          value: String(t >= alert ? 1 : 0),
        },
        {
          label: tr("visual.live.profit"),
          value: usd(earned),
          positive: earned > 0.004,
        },
      ];
    },
    strip: {
      icon: "bell",
      title: tr("visual.tradingview.strip_title"),
      items: [
        { value: "ETHUSDT · 15m" },
        { label: tr("visual.tradingview.condition") },
        { label: tr("visual.tradingview.webhook") },
      ],
      at: [0.15, alert, alert + 0.7],
    },
  };
};

export const STORIES: Record<VisualMethod, (tr: Tr) => Story> = {
  grid: gridStory,
  signals: signalsStory,
  dca: dcaStory,
  tradingview: tradingViewStory,
};
